import {dsDtos} from '@vectronic/openApi-customer'
import {Buffer} from 'buffer'
import {isValidPhoneNumber} from 'libphonenumber-js'
import {Error} from './error'

export class Code {
  static do(dto: dsDtos.Parameter, value?: string | null) {
    if (value == null) return this.handleNull(dto)
    const buffer = Buffer.alloc(dto.length!)
    const offset = dto.value === 'TIME_ZONE' ? -128 : (dto.offset ?? 0)
    const factor = dto.factor ?? 1
    return (
      this.encodeInteger(dto, value, buffer, offset, factor) ??
      this.encodeSignedAndFloat(dto, value, buffer, offset, factor) ??
      this.encodeTextAndDate(dto, value, buffer) ??
      this.encodeComposite(dto, value, buffer) ??
      this.encodeHexAndPattern(dto, value, buffer) ??
      this.encodeUnsupported(dto) ??
      this.encodeList(dto, value, buffer) ??
      this.encodeGlobalstar(dto, value, buffer)
    )
  }

  private static encodeInteger(dto: dsDtos.Parameter, value: string, buffer: Buffer, offset: number, factor: number) {
    switch (dto.value) {
      case 'TIME_ZONE':
      case 'UNSIGNED_INT':
      case 'TIME':
      case 'DAYTIME':
      case 'DATE_TIME':
      case 'RANGE':
      case 'TIME_SPAN':
      case 'MESSAGE_MODE':
      case 'FLAGS':
      case 'ENUMERATION':
      case 'BAND_FLAGS':
        buffer.writeUIntLE(this.toInt(value) / factor - offset, 0, dto.length!)
        return buffer
      default:
        return undefined
    }
  }

  private static encodeSignedAndFloat(
    dto: dsDtos.Parameter,
    value: string,
    buffer: Buffer,
    offset: number,
    factor: number,
  ) {
    switch (dto.value) {
      case 'SIGNED_INT':
        buffer.writeIntLE(this.toInt(value) / factor - offset, 0, dto.length!)
        return buffer
      case 'FLOAT':
        buffer.writeFloatLE(this.toFloat(value) / factor - offset)
        return buffer
      default:
        return undefined
    }
  }

  private static encodeTextAndDate(dto: dsDtos.Parameter, value: string, buffer: Buffer) {
    switch (dto.value) {
      case 'PHONE':
      case 'STRING':
        if (dto.value === 'PHONE' && !isValidPhoneNumber(value)) throw Error.INVALID_PHONENUMBER
        buffer.write(value, 'utf8')
        return buffer
      case 'DATE':
        return this.date(value, buffer)
      default:
        return undefined
    }
  }

  private static encodeComposite(dto: dsDtos.Parameter, value: string, buffer: Buffer) {
    switch (dto.value) {
      case 'DATE_TIME_AND_UNSIGNED_INT': {
        const split = value.split(',')
        buffer.writeUInt32LE(this.toInt(split[0]))
        buffer.writeUIntLE(this.toInt(split[1]), 4, dto.length! - 4)
        return buffer
      }
      case 'IP_ADDRESS':
      case 'VERSION_1_1_1_1': {
        const split = value.split('.')
        buffer.writeUInt8(this.toInt(split[0]), 3)
        buffer.writeUInt8(this.toInt(split[1]), 2)
        buffer.writeUInt8(this.toInt(split[2]), 1)
        buffer.writeUInt8(this.toInt(split[3]))
        return buffer
      }
      case 'VERSION_2_2': {
        const split = value.split('.')
        buffer.writeUInt16LE(this.toInt(split[0]), 2)
        buffer.writeUInt16LE(this.toInt(split[1]))
        return buffer
      }
      case 'VERSION_2_1_1': {
        const split = value.split('.')
        buffer.writeUInt16LE(this.toInt(split[0]), 2)
        buffer.writeUInt8(this.toInt(split[1]), 1)
        buffer.writeUInt8(this.toInt(split[2]))
        return buffer
      }
      case 'DATE_AND_VERSION_1_1_1': {
        const split = value.split(',')
        this.date(split[0], buffer)
        const versionSplit = split[1].split('.')
        buffer.writeUInt8(this.toInt(versionSplit[2]), 3)
        buffer.writeUInt8(this.toInt(versionSplit[1]), 4)
        buffer.writeUInt8(this.toInt(versionSplit[0]), 5)
        return buffer
      }
      default:
        return undefined
    }
  }

  private static encodeHexAndPattern(dto: dsDtos.Parameter, value: string, buffer: Buffer) {
    switch (dto.value) {
      case 'HEX':
        buffer.write(value, 'hex')
        return buffer
      case 'HEX_DOT':
        buffer.write(value.split('.').join(''), 'hex')
        return buffer
      case 'VHF_PATTERN': {
        const split = value.split(',')
        buffer.writeUInt8(this.toInt(split[0]))
        buffer.writeUInt8(this.toInt(split[1]), 1)
        buffer.writeUInt8(this.toInt(split[2]) / 50, 2)
        return buffer
      }
      default:
        return undefined
    }
  }

  private static encodeUnsupported(dto: dsDtos.Parameter) {
    switch (dto.value) {
      case 'CALIBRATION_BIAS':
        throw `${Error.NOT_IMPLEMENTED}: CALIBRATION_BIAS` //TODO
      case 'CALIBRATION_MATRIX':
        throw `${Error.NOT_IMPLEMENTED}: CALIBRATION_MATRIX.` //TODO
      default:
        return undefined
    }
  }

  private static encodeList(dto: dsDtos.Parameter, value: string, buffer: Buffer) {
    switch (dto.value) {
      case 'LIST_UNSIGNED_INT_4': {
        const split = value.split(',')
        for (let i = 0; i < split.length; i++) buffer.writeUInt32LE(this.toInt(split[i]), i * 4)
        this.fillRemaining(buffer, split.length * 4)
        return buffer
      }
      case 'LIST_UNSIGNED_INT_2': {
        const split = value.split(',')
        for (let i = 0; i < split.length; i++) buffer.writeUInt16LE(this.toInt(split[i]), i * 2)
        this.fillRemaining(buffer, split.length * 2)
        return buffer
      }
      case 'LIST_DATE_TIME_AND_HEX_8': {
        const split = value.split(',')
        let bufferOffset = 0
        for (let i = 0; i < split.length; i++) {
          buffer.writeUInt32LE(this.toInt(split[i++]), bufferOffset)
          bufferOffset += 4
          buffer.writeUInt32BE(this.toInt(split[i], 16), bufferOffset)
          bufferOffset += 4
        }
        this.fillRemaining(buffer, split.length * 8)
        return buffer
      }
      default:
        return undefined
    }
  }

  private static fillRemaining(buffer: Buffer, fromOffset: number) {
    for (let i = fromOffset; i < buffer.length; i++) buffer[i] = 0xff
  }

  private static encodeGlobalstar(dto: dsDtos.Parameter, value: string, buffer: Buffer) {
    if (dto.value === 'GLOBALSTAR_ESN') {
      const split = value.split('-')
      buffer.writeUIntLE(this.toInt(split[1]), 0, 3)
      buffer.writeUInt8(this.toInt(split[0]), 3)
      return buffer
    }
    return undefined
  }

  private static toFloat(s: string) {
    if (!/^[-.+e\d]+$/.test(s)) throw Error.NAN
    const float = parseFloat(s)
    if (isNaN(float)) throw Error.NAN
    return float
  }

  private static toInt(s: string, radix?: number) {
    const float = this.toFloat(s)
    if (!Number.isInteger(float)) throw Error.NOT_A_INT
    return parseInt(s, radix)
  }

  private static date(data: string, buffer: Buffer) {
    const milliSecondsInADay = 1000 * 60 * 60 * 24
    const daysSince2000 = this.toInt(data)
    const date = new Date(Date.UTC(2000, 0) + daysSince2000 * milliSecondsInADay)
    buffer.writeUInt8(date.getUTCFullYear() - 2000, 0)
    buffer.writeUInt8(date.getUTCMonth() + 1, 1)
    buffer.writeUInt8(date.getUTCDate(), 2)
    return buffer
  }

  private static handleNull(dto: dsDtos.Parameter) {
    if (dto.value === 'FLAGS') throw Error.INVALID_INPUT
    if (dto.value === 'SIGNED_INT') throw Error.INVALID_INPUT
    const buffer = Buffer.alloc(dto.length!)
    buffer.fill(255)
    return buffer
  }
}
